"use client";

import { AnimatePresence } from "framer-motion";
import { forwardRef, useEffect, useImperativeHandle, useRef, type ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import {useGailLauncherShortcutHint} from "@/hooks/useGailLauncherShortcut";
import {useLauncherOnboarding} from "@/hooks/useLauncherOnboarding";
import GailAssistantLauncherButton from "./GailAssistantLauncherButton";
import GailLauncherTip from "./GailLauncherTip";

type Props = Omit<ComponentPropsWithoutRef<typeof GailAssistantLauncherButton>, "label" | "placement"> & {
  chatOpen?: boolean;
};

const GailTopbarButton = forwardRef<HTMLButtonElement, Props>(function GailTopbarButton({ chatOpen = false, ...props }, ref) {
  const { t } = useTranslation("ask");
  const {ariaKeyShortcuts} = useGailLauncherShortcutHint();
  const buttonRef = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => buttonRef.current!);
  const {tipVisible, acknowledgeTip} = useLauncherOnboarding(chatOpen, {suppressAfterTour: false});
  useEffect(() => {
    // Also settle the introduction when a shortcut or page action opens chat.
    if (chatOpen) acknowledgeTip("open");
  }, [chatOpen, acknowledgeTip]);
  const label = t("launcher.label");
  return (
    <div className="relative inline-flex shrink-0" hidden={chatOpen} style={chatOpen ? {display: "none"} : undefined}>
      <div role="status" aria-atomic="true" className="sr-only">
        {tipVisible ? `${t("launcher.tip.title")} ${t("launcher.tip.body")}` : ""}
      </div>
      <GailAssistantLauncherButton
        {...props}
        ref={buttonRef}
        label={label}
        placement="topbar"
        highlighted={tipVisible}
        aria-keyshortcuts={ariaKeyShortcuts}
        onClick={(event) => {
          acknowledgeTip("open");
          props.onClick?.(event);
        }}
      />
      <AnimatePresence>
        {tipVisible && <GailLauncherTip
          placement="topbar"
          onDismiss={() => {
            buttonRef.current?.focus({preventScroll: true});
            acknowledgeTip("dismiss");
          }}
          onTry={(event) => {
            acknowledgeTip("try");
            props.onClick?.(event);
          }}
        />}
      </AnimatePresence>
    </div>
  );
});

export default GailTopbarButton;
